import { useSearchParams } from 'react-router-dom';
import { useCallback } from 'react';

export interface PaginationState {
  page: number;
  pageSize: number;
  from: number;
  to: number;
}

export interface PaginationControls extends PaginationState {
  setPage: (page: number) => void;
  setPageSize: (size: number) => void;
  nextPage: () => void;
  prevPage: () => void;
  canPrev: boolean;
  canNext: (totalCount: number | null) => boolean;
  totalPages: (totalCount: number | null) => number;
}

const DEFAULT_PAGE_SIZE = 50;
export const PAGE_SIZE_OPTIONS = [25, 50, 100, 200];

export function usePagination(
  _storageKey: string, // TODO: per-page pageSize preference persistence
  defaultPageSize: number = DEFAULT_PAGE_SIZE,
): PaginationControls {
  const [searchParams, setSearchParams] = useSearchParams();

  const page = Math.max(1, parseInt(searchParams.get('page') ?? '1', 10));
  const pageSize = (() => {
    const raw = parseInt(searchParams.get('pageSize') ?? String(defaultPageSize), 10);
    return PAGE_SIZE_OPTIONS.includes(raw) ? raw : defaultPageSize;
  })();

  const from = (page - 1) * pageSize;
  const to = from + pageSize - 1; // Supabase .range() is inclusive on both ends

  const setPage = useCallback((newPage: number) => {
    setSearchParams(prev => {
      const next = new URLSearchParams(prev);
      if (newPage <= 1) next.delete('page');
      else next.set('page', String(newPage));
      return next;
    }, { replace: false });
  }, [setSearchParams]);

  const setPageSize = useCallback((newSize: number) => {
    setSearchParams(prev => {
      const next = new URLSearchParams(prev);
      if (newSize === defaultPageSize) next.delete('pageSize');
      else next.set('pageSize', String(newSize));
      next.delete('page');
      return next;
    }, { replace: false });
  }, [setSearchParams, defaultPageSize]);

  const nextPage = useCallback(() => setPage(page + 1), [page, setPage]);
  const prevPage = useCallback(() => setPage(Math.max(1, page - 1)), [page, setPage]);

  const totalPages = useCallback((totalCount: number | null): number => {
    if (totalCount == null) return 1;
    return Math.max(1, Math.ceil(totalCount / pageSize));
  }, [pageSize]);

  const canPrev = page > 1;
  const canNext = useCallback((totalCount: number | null): boolean => {
    if (totalCount == null) return false;
    return page < totalPages(totalCount);
  }, [page, totalPages]);

  return { page, pageSize, from, to, setPage, setPageSize, nextPage, prevPage, canPrev, canNext, totalPages };
}
